"use client";

import { useEffect, useState, useRef } from "react";
import { Calendar } from "lucide-react";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";
import { CalPopup } from "./cal-popup";
import { WhatsappBubble } from "@/components/whatsapp/whatsapp-bubble";

interface CalFloatingButtonProps {
  // Type d'événement à réserver, si différent du call de découverte par défaut
  // (ex : les pages de proposition pointent vers un point technique).
  calLink?: string;
  // Textes fixes, à passer sur les pages non couvertes par le sélecteur de
  // langue du site (ex : /[slug], toujours en français) : useV2Translation
  // part de "en" tant que l'URL n'a pas de préfixe /fr détectable, ce qui
  // afficherait "Book" au lieu de "Réserver" sans ce contournement.
  label?: string;
  ariaLabelOpen?: string;
  ariaLabelClose?: string;
  // Force la langue de l'interface Cal.com elle-même (voir CalInlineEmbed).
  locale?: string;
}

export function CalFloatingButton({
  calLink,
  label,
  ariaLabelOpen,
  ariaLabelClose,
  locale,
}: CalFloatingButtonProps = {}) {
  const { t } = useV2Translation();
  const [isOpen, setIsOpen] = useState(false);
  const hasAutoOpened = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (hasAutoOpened.current) return;

      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? scrollTop / docHeight : 0;

      if (scrollPercent >= 0.5) {
        hasAutoOpened.current = true;
        setIsOpen(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <CalPopup isOpen={isOpen} onClose={() => setIsOpen(false)} calLink={calLink} locale={locale} />

      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
      <WhatsappBubble />
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center gap-3 px-5 py-4 bg-navy text-paper border border-paper/20 rounded-full shadow-2xl shadow-navy/40 font-mono text-xs uppercase tracking-widest hover:bg-gold hover:text-navy hover:border-gold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-background"
        aria-label={
          isOpen
            ? (ariaLabelClose ?? t.booking.ariaLabelClose)
            : (ariaLabelOpen ?? t.booking.ariaLabelOpen)
        }
        aria-expanded={isOpen}
      >
        <Calendar size={18} strokeWidth={2.2} />
        <span>{label ?? t.booking.buttonLabel}</span>
      </button>
      </div>
    </>
  );
}
